import { useEffect, useState, useCallback } from "react";
import { api, apiError, timeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PaperPlaneRight, Megaphone } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function ParentMessages() {
  const { user } = useAuth();
  const [thread, setThread] = useState([]);
  const [anns, setAnns] = useState([]);
  const [draft, setDraft] = useState("");

  const load = useCallback(async () => {
    const [m, a] = await Promise.all([api.get("/messages"), api.get("/announcements")]);
    setThread(m.data);
    setAnns(a.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!draft.trim()) return;
    try {
      await api.post("/messages", { to_email: user.email, body: draft });
      setDraft("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="parent-messages">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Communication</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Messages</h1>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-border p-6 flex flex-col min-h-[28rem]">
          <p className="font-display text-xl font-semibold">Teach Play to Learn staff</p>
          <div className="mt-5 flex-1 space-y-3 max-h-[24rem] overflow-y-auto" data-testid="parent-thread">
            {thread.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No messages yet. Tell us about a late pickup, an absence, or anything else.
              </p>
            )}
            {thread.map((m) => {
              const mine = m.from_email === user?.email;
              return (
                <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  mine ? "ml-auto bg-[#3B82F6] text-white" : "bg-muted"}`}
                     data-testid={`parent-message-${m.id}`}>
                  <p className="text-sm">{m.body}</p>
                  <p className={`text-xs mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                    {m.from_name} · {timeOf(m.at)}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-5 flex gap-3">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && send()}
                   placeholder="Message the front office…" className="h-12 rounded-xl"
                   data-testid="parent-message-input" />
            <Button onClick={send} className="h-12 px-6 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="parent-message-send">
              <PaperPlaneRight size={18} />
            </Button>
          </div>
        </div>

        <div className="space-y-3" data-testid="parent-announcements">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Megaphone size={22} weight="duotone" className="text-[#FBBF24]" /> Classroom announcements
          </h2>
          {anns.length === 0 && <p className="text-muted-foreground">Nothing announced yet.</p>}
          {anns.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-border p-6"
                 data-testid={`parent-announcement-${a.id}`}>
              <p className="font-display text-lg font-semibold">{a.title}</p>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">{a.room}</p>
              <p className="mt-3 text-base text-muted-foreground">{a.body}</p>
              <p className="mt-3 text-xs text-muted-foreground">{a.from_name} · {timeOf(a.at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
